import { ChangeDetectionStrategy, Component, effect, inject, untracked } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ModalComponent } from '@shared/components/ui/modal';
import { ButtonComponent } from '@shared/components/ui/button';
import { SupportMessageComponent } from '@shared/components/ui/support-message';
import { TemporaryAuthModalService } from './temporary-auth-modal.service';
import { TranslateModule } from '@ngx-translate/core';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import unlockedAnimation from './Unlocked.json';
import { AnimationItem } from 'lottie-web';
import { SelfUserStore } from '@person/user/self/data-access';
import { InputComponent } from '@shared/components/ui/input';
import { InputWrapperComponent } from '@shared/components/ui/input-wrapper';

@Component({
  selector: 'lib-temporary-auth-modal',
  standalone: true,
  imports: [
    ModalComponent,
    ReactiveFormsModule,
    ButtonComponent,
    SupportMessageComponent,
    TranslateModule,
    LottieComponent,
    InputComponent,
    InputWrapperComponent,
  ],
  templateUrl: './temporary-auth-modal.component.html',
  styleUrl: './temporary-auth-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemporaryAuthModalComponent {
  private readonly modalService = inject(TemporaryAuthModalService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly userSelfStore = inject(SelfUserStore);
  public animationItem: AnimationItem;
  public readonly state = this.modalService.state;
  public animationFrame: any;

  private userNowHasPermission = false;

  private readonly userHasImpersonatedUserProfile = effect(() => {
    const profile = this.userSelfStore.impersonatedUserProfile();
    const userPermissions = profile?.ClientPermissions?.filter(
      (permission) => permission.Checked,
    );
    const userNowHasPermission = userPermissions?.some(
      (permission) => this.state().requiredPermissions.includes(permission.Permission),
    );
    const isOwner = profile?.IsOwner;
    if (userNowHasPermission || isOwner ) {
      untracked(() => {
        this.userNowHasPermission = true;
        this.continueAnimation();
      });
      return;
    }
    this.userNowHasPermission = false;
  });

  public showPassword = false;

  public readonly lottieOptions: AnimationOptions = {
    animationData: unlockedAnimation as unknown as object,
    autoplay: true,
    loop: false,
  };

  public readonly form = this.formBuilder.group({
    Email: ['', [Validators.required, Validators.email]],
    Password: ['', [Validators.required]],
  });

  constructor() {
    effect(() => {
      const { visible, authSuccess } = this.state();

      if (!visible) {
        this.form.reset();
        this.showPassword = false;
        this.userNowHasPermission = false;

        if (this.animationItem) {
          this.animationItem.stop();
        }
        this.animationFrame = undefined;
        return;
      }

      if (visible && this.animationItem && typeof this.animationFrame === 'number') {
        this.userNowHasPermission = false;
        this.animationItem.goToAndStop(this.animationFrame, true);
      }

      if (authSuccess) {
        this.userNowHasPermission = true;
        this.continueAnimation();
      }
    });
  }

  public enterFrame({currentTime}: any): void {
    this.animationFrame = currentTime;
    if (this.animationFrame >=30 && !this.userNowHasPermission) {
      this.animationItem.pause();
    }
  }

  public continueAnimation(): void {
    if (!this.animationItem) return;
    this.animationItem.play();
  }

  public createAnimation(animationItem: AnimationItem): void {
    this.animationItem = animationItem;
      if (typeof this.animationFrame === 'number') {
        this.animationItem.goToAndStop(this.animationFrame, true);
      }
  }

  public close(): void {
    this.modalService.cancel();
  }

  public authenticate(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    const { Email, Password } = this.form.value;
    this.modalService.authenticate(Email ?? '', Password ?? '');
  }
}
